import React, { useState, useCallback, useEffect } from 'react';
import { getFunnel, getEventNames } from '../api';
import ChartCard from '../components/ChartCard';

// ── Preset funnels ────────────────────────────────────────────────────────
const PRESETS = [
  {
    label: 'App Onboarding',
    icon: '🚀',
    steps: [
      { name: 'App Open',       eventName: 'session_start' },
      { name: 'First Screen',   eventName: 'screen_view' },
      { name: 'Sign Up Start',  eventName: 'sign_up' },
      { name: 'Tutorial Done',  eventName: 'tutorial_complete' },
    ],
  },
  {
    label: 'Purchase Flow',
    icon: '🛒',
    steps: [
      { name: 'View Item',      eventName: 'view_item' },
      { name: 'Add to Cart',    eventName: 'add_to_cart' },
      { name: 'Begin Checkout', eventName: 'begin_checkout' },
      { name: 'Purchase',       eventName: 'purchase' },
    ],
  },
  {
    label: 'Ad Engagement',
    icon: '📢',
    steps: [
      { name: 'Ad Impression',  eventName: 'ad_impression' },
      { name: 'Ad Click',       eventName: 'ad_click' },
      { name: 'Screen View',    eventName: 'screen_view' },
      { name: 'Purchase',       eventName: 'purchase' },
    ],
  },
  {
    label: 'User Activation',
    icon: '✨',
    steps: [
      { name: 'Session Start',  eventName: 'session_start' },
      { name: 'Screen View',    eventName: 'screen_view' },
      { name: 'Key Action',     eventName: 'select_content' },
      { name: 'Return Visit',   eventName: 'session_start' },
    ],
  },
];

const COLORS = ['#38bdf8','#818cf8','#34d399','#f472b6','#fb923c','#fbbf24','#a78bfa','#22d3ee'];

function pctColor(p) {
  if (p >= 70) return 'var(--success)';
  if (p >= 40) return 'var(--warning)';
  return 'var(--danger)';
}

export default function Funnel({ propertyId, startDate, endDate }) {
  const [steps,       setSteps]       = useState(PRESETS[0].steps.map(s=>({...s})));
  const [result,      setResult]      = useState(null);
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState(null);
  const [eventNames,  setEventNames]  = useState([]);
  const [evLoading,   setEvLoading]   = useState(false);
  const [activePreset,setActivePreset]= useState(0);
  const [filterStep,  setFilterStep]  = useState('all');
  const [minConv,     setMinConv]     = useState(0);

  // Load available event names for autocomplete
  useEffect(() => {
    if (!propertyId) return;
    setEvLoading(true);
    getEventNames(propertyId, startDate, endDate)
      .then(r => {
        const raw = r.data.events || [];
        // Normalize: server may return strings or {name,count} objects
        setEventNames(raw.map(e => (typeof e === 'string' ? e : e.name || String(e))));
      })
      .catch(console.error)
      .finally(() => setEvLoading(false));
  }, [propertyId, startDate, endDate]);

  const runFunnel = useCallback(async () => {
    if (!propertyId || steps.length < 2) return;
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const res = await getFunnel(propertyId, startDate, endDate, steps);
      setResult(res.data.steps);
    } catch(e) {
      setError(e.response?.data?.error || e.message);
    }
    setLoading(false);
  }, [propertyId, startDate, endDate, steps]);

  const applyPreset = (i) => {
    setActivePreset(i);
    setSteps(PRESETS[i].steps.map(s=>({...s})));
    setResult(null);
  };

  const addStep = () => setSteps(prev => [...prev, { name:'New Step', eventName:'' }]);
  const removeStep = (i) => setSteps(prev => prev.filter((_,j)=>j!==i));
  const updateStep = (i, field, val) => setSteps(prev => prev.map((s,j) => j===i?{...s,[field]:val}:s));

  // filtered result rows
  const filteredResult = result ? result.filter(r => {
    if (filterStep !== 'all' && r.step !== parseInt(filterStep)) return false;
    if (r.overallPct < minConv) return false;
    return true;
  }) : [];

  const topCount = result?.[0]?.count || 0;
  const finalConv = result && result.length > 0 ? result[result.length-1].overallPct : 0;

  // Show loading state
  if (loading) {
    return (
      <div style={{ display:'flex', flexDirection:'column', gap:20 }}>
        <div style={{ ...styles.section, textAlign:'center', padding:40 }}>
          <div style={{ fontSize: 48, color: 'var(--accent)', animation: 'spin 1s linear infinite', marginBottom:16 }}>⊙</div>
          <div style={{ color: 'var(--text3)', fontSize: 13 }}>Analyzing funnel data...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:20 }}>

      {/* ── Preset selector ──────────────────────── */}
      <div style={styles.section}>
        <div style={styles.sectionTitle}>⚡ Funnel Templates</div>
        <div style={styles.presetRow}>
          {PRESETS.map((p,i) => (
            <button key={i}
              style={{ ...styles.presetBtn, ...(activePreset===i ? styles.presetBtnActive : {}) }}
              onClick={() => applyPreset(i)}>
              <span style={styles.presetIcon}>{p.icon}</span>
              <span style={{fontSize:'clamp(11px, 2vw, 13px)'}}>{p.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* ── Funnel Builder ───────────────────────── */}
      <div style={styles.section}>
        <div style={styles.sectionHeader}>
          <div style={styles.sectionTitle}>🔧 Funnel Steps</div>
          <button style={styles.addBtn} onClick={addStep} disabled={steps.length >= 8}>+ Add Step</button>
        </div>
        <div style={styles.stepsBuilder}>
          {steps.map((step, i) => (
            <div key={i} style={styles.stepRow}>
              <div style={{ ...styles.stepNum, background: COLORS[i%COLORS.length]+'22', color: COLORS[i%COLORS.length], border:`1px solid ${COLORS[i%COLORS.length]}44` }}>
                {i+1}
              </div>
              {i > 0 && <div style={styles.stepArrow}>↓</div>}
              <div style={styles.stepInputs}>
                <input
                  style={styles.stepNameInput}
                  placeholder="Step name"
                  value={step.name}
                  onChange={e => updateStep(i,'name',e.target.value)}
                />
                <div style={styles.eventInputWrap}>
                  <input
                    style={styles.eventInput}
                    placeholder="GA4 event name (e.g. screen_view)"
                    value={step.eventName}
                    onChange={e => updateStep(i,'eventName',e.target.value)}
                    list={`ev-list-${i}`}
                  />
                  <datalist id={`ev-list-${i}`}>
                    {eventNames.map((en,idx) => {
                    const val = typeof en === 'string' ? en : (en?.name || '');
                    return <option key={idx} value={val} />;
                  })}
                  </datalist>
                  {evLoading && <div style={styles.evLoadHint}>loading…</div>}
                </div>
                <button style={styles.removeBtn} onClick={() => removeStep(i)}>✕</button>
              </div>
            </div>
          ))}
        </div>
        {error && <div style={styles.errMsg}>Error: {error}</div>}
        <button style={styles.runBtn} onClick={runFunnel}>Run Funnel Analysis</button>
      </div>

      {/* ── Results (only show if data exists and not loading) ────────────── */}
      {result && result.length > 0 ? (
        <>
          {/* Filter bar */}
          <div style={styles.filterBar}>
            <div style={styles.filterGroup}>
              <label style={styles.filterLabel}>Filter by Step:</label>
              <select style={styles.filterSel} value={filterStep} onChange={e=>setFilterStep(e.target.value)}>
                <option value="all">All Steps</option>
                {result.map((r,i)=><option key={i} value={r.step}>{r.step}. {r.name}</option>)}
              </select>
            </div>
            <div style={styles.filterGroup}>
              <label style={styles.filterLabel}>Min Conversion:</label>
              <input type="number" style={{...styles.filterSel, width:70}} min="0" max="100" value={minConv} onChange={e=>setMinConv(+e.target.value)} />
              <span style={{fontSize:11,color:'var(--text3)'}}>%</span>
            </div>
            <div style={styles.convSummary}>
              <span style={styles.convLabel}>Overall Conversion:</span>
              <span style={{...styles.convVal, color:pctColor(finalConv)}}>{finalConv}%</span>
            </div>
          </div>

          {/* KPI strip */}
          <div style={styles.kpiStrip}>
            <div style={styles.kpiItem}>
              <div style={styles.kpiVal}>{topCount?.toLocaleString() || '—'}</div>
              <div style={styles.kpiLbl}>Step 1 Entry</div>
            </div>
            <div style={styles.kpiDiv}>↓</div>
            <div style={styles.kpiItem}>
              <div style={styles.kpiVal}>{(result[result.length-1]?.count||0)?.toLocaleString()}</div>
              <div style={styles.kpiLbl}>Final Step</div>
            </div>
            <div style={styles.kpiDiv}>↓</div>
            <div style={styles.kpiItem}>
              <div style={{...styles.kpiVal, color:pctColor(finalConv)}}>{finalConv}%</div>
              <div style={styles.kpiLbl}>Total Conv.</div>
            </div>
          </div>

          {/* Funnel visualization */}
          <div style={styles.funnelViz}>
            {result.map((r,i)=>(
              <div key={i} style={styles.funnelStep}>
                <div style={styles.funnelStepHeader}>
                  <div style={{ ...styles.funnelStepNum, background: COLORS[i%COLORS.length]+'22', color: COLORS[i%COLORS.length], border:`1px solid ${COLORS[i%COLORS.length]}44` }}>
                    {i+1}
                  </div>
                  <div style={styles.funnelStepInfo}>
                    <div style={styles.funnelStepName}>{r.name}</div>
                    <div style={styles.funnelEventName}>{r.eventName}</div>
                  </div>
                  <div style={styles.funnelStepStats}>
                    <span style={styles.funnelCount}>{r.count?.toLocaleString()}</span>
                    <div style={styles.funnelUsers}>{r.users?.toLocaleString()} users</div>
                  </div>
                </div>
                <div style={styles.funnelBarTrack}>
                  <div style={{ ...styles.funnelBar, width:`${(r.count/topCount)*100}%`, background: COLORS[i%COLORS.length] }}>
                    <span style={styles.funnelBarLabel}>{((r.count/topCount)*100).toFixed(0)}%</span>
                  </div>
                </div>
                {i < result.length-1 && (
                  <div style={styles.dropBadge}>
                    <span style={{ color:'var(--danger)', fontWeight:700 }}>↓ {r.dropPct}% drop</span>
                    <span style={{ color:'var(--text3)' }}> · {r.convPct}% passed</span>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Step-by-step table */}
          <ChartCard title="Funnel Step Detail" subtitle="Complete breakdown with conversion rates">
            <div style={styles.tableWrap}>
              <div style={styles.thead}>
                {['Step','Name','Event','Count','Users','Step Conv%','Overall%','Drop-off'].map(h=>(
                  <span key={h}>{h}</span>
                ))}
              </div>
              {filteredResult.map((r,i) => (
                <div key={i} style={{...styles.trow, background:i%2===0?'transparent':'rgba(99,179,237,0.02)'}}>
                  <span style={{...styles.stepBadge, background:COLORS[i%COLORS.length]+'22', color:COLORS[i%COLORS.length]}}>{r.step}</span>
                  <span style={{color:'var(--text)',fontWeight:600}}>{r.name}</span>
                  <span style={{color:'var(--accent)',fontFamily:'Space Mono,monospace',fontSize:10}}>{r.eventName}</span>
                  <span style={{fontFamily:'Space Mono,monospace',fontWeight:700}}>{r.count?.toLocaleString()}</span>
                  <span style={{fontFamily:'Space Mono,monospace'}}>{r.users?.toLocaleString()}</span>
                  <span style={{color:pctColor(r.convPct),fontWeight:700}}>{r.step===1?'100':r.convPct}%</span>
                  <span style={{color:pctColor(r.overallPct),fontWeight:700}}>{r.overallPct}%</span>
                  <span style={{color:'var(--danger)',fontFamily:'Space Mono,monospace'}}>{r.step===1?'—':`-${r.dropPct}%`}</span>
                </div>
              ))}
            </div>
          </ChartCard>

          {/* Insights */}
          <div style={styles.insightsBox}>
            <div style={styles.insightsTitle}>💡 Funnel Insights</div>
            <div style={styles.insightsList}>
              {result.slice(1).map((r,i) => {
                const prev = result[i];
                const lost = prev.count - r.count;
                if (r.dropPct < 5) return null;
                return (
                  <div key={i} style={{ ...styles.insightItem, borderColor: r.dropPct>50?'var(--danger)33':r.dropPct>25?'var(--warning)33':'var(--border)' }}>
                    <span style={{ fontSize:18 }}>{r.dropPct>50?'🔴':r.dropPct>25?'🟡':'🟢'}</span>
                    <div>
                      <div style={{ fontSize:13, fontWeight:600, color:'var(--text)', marginBottom:4 }}>
                        {prev.name} → {r.name}: <span style={{ color:'var(--danger)' }}>{r.dropPct}% drop-off</span>
                      </div>
                      <div style={{ fontSize:12, color:'var(--text2)', lineHeight:1.6 }}>
                        {lost?.toLocaleString()} users left at this step. {' '}
                        {r.dropPct>50
                          ? `Critical bottleneck — more than half of users drop here. Investigate UX friction, slow load times, or confusing UI at "${r.name}".`
                          : r.dropPct>25
                          ? `Significant drop — consider A/B testing this step or adding contextual prompts to guide users through "${r.name}".`
                          : `Moderate drop — normal for this step type. Monitor over time for regression.`
                        }
                      </div>
                    </div>
                  </div>
                );
              }).filter(Boolean)}
              {result[result.length-1].overallPct > 50 && (
                <div style={{ ...styles.insightItem, borderColor:'var(--success)33' }}>
                  <span style={{ fontSize:18 }}>🌟</span>
                  <div>
                    <div style={{ fontSize:13, fontWeight:600, color:'var(--success)', marginBottom:4 }}>Excellent Funnel Performance</div>
                    <div style={{ fontSize:12, color:'var(--text2)', lineHeight:1.6 }}>
                      {finalConv}% overall conversion is above industry average. Focus on growing top-of-funnel traffic to maximise completions.
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}

const styles = {
  section:      { background:'var(--surface)', border:'1px solid var(--border)', borderRadius:14, padding:20 },
  sectionTitle: { fontSize:'clamp(12px, 2.5vw, 13px)', fontWeight:700, color:'var(--text)', marginBottom:14 },
  sectionHeader:{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14, flexWrap:'wrap', gap:12 },

  presetRow:  { display:'flex', gap:10, flexWrap:'wrap' },
  presetBtn:  { display:'flex', alignItems:'center', gap:8, padding:'9px 16px', background:'var(--surface2)', border:'1px solid var(--border)', borderRadius:10, color:'var(--text2)', fontSize:'clamp(11px, 2vw, 13px)', fontWeight:500, cursor:'pointer', transition:'all 0.15s' },
  presetBtnActive: { background:'rgba(56,189,248,0.15)', borderColor:'var(--accent)', color:'var(--accent)' },
  presetIcon: { fontSize:'clamp(14px, 3vw, 16px)' },

  stepsBuilder: { display:'flex', flexDirection:'column', gap:0 },
  stepRow:    { display:'flex', alignItems:'center', gap:12, padding:'8px 0', position:'relative', flexWrap:'wrap' },
  stepNum:    { width:32, height:32, borderRadius:8, display:'flex', alignItems:'center', justifyContent:'center', fontWeight:800, fontSize:'clamp(12px, 2vw, 14px)', flexShrink:0 },
  stepArrow:  { position:'absolute', left:11, top:-14, fontSize:16, color:'var(--text3)' },
  stepInputs: { display:'flex', gap:10, flex:1, minWidth:200, flexWrap:'wrap' },
  stepNameInput:{ flex:1, minWidth:120, background:'var(--surface2)', border:'1px solid var(--border)', borderRadius:8, color:'var(--text)', padding:'9px 12px', fontSize:'clamp(11px, 2vw, 12px)', outline:'none', fontFamily:'Sora,sans-serif' },
  eventInputWrap:{ flex:1.5, minWidth:150, position:'relative' },
  eventInput: { width:'100%', background:'var(--surface2)', border:'1px solid var(--border)', borderRadius:8, color:'var(--text)', padding:'9px 12px', fontSize:'clamp(10px, 1.8vw, 12px)', outline:'none', fontFamily:'Space Mono,monospace', boxSizing:'border-box' },
  evLoadHint: { position:'absolute', right:8, top:'50%', transform:'translateY(-50%)', fontSize:10, color:'var(--text3)' },
  removeBtn:  { background:'none', border:'1px solid var(--danger)33', borderRadius:6, color:'var(--danger)', width:28, height:28, cursor:'pointer', flexShrink:0, fontSize:12 },
  addBtn:     { padding:'8px 16px', background:'var(--surface2)', border:'1px solid var(--border)', borderRadius:8, color:'var(--accent)', fontSize:'clamp(11px, 2vw, 12px)', fontWeight:600, cursor:'pointer' },
  runBtn:     { marginTop:16, padding:'12px 28px', background:'linear-gradient(135deg,var(--accent2),var(--accent))', border:'none', borderRadius:10, color:'#fff', fontWeight:700, fontSize:'clamp(12px, 2vw, 14px)', cursor:'pointer', boxShadow:'0 4px 16px rgba(56,189,248,0.25)' },
  errMsg:     { marginTop:10, color:'var(--danger)', fontSize:'clamp(10px, 1.8vw, 12px)' },

  filterBar:   { background:'var(--surface)', border:'1px solid var(--border)', borderRadius:12, padding:'12px 18px', display:'flex', alignItems:'center', gap:16, flexWrap:'wrap' },
  filterLabel: { fontSize:'clamp(10px, 1.8vw, 11px)', color:'var(--text3)', fontWeight:600 },
  filterSel:   { background:'var(--surface2)', border:'1px solid var(--border)', borderRadius:8, color:'var(--text)', padding:'7px 10px', fontSize:'clamp(10px, 1.8vw, 12px)', outline:'none' },
  filterGroup: { display:'flex', alignItems:'center', gap:8 },
  convSummary: { marginLeft:'auto', display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' },
  convLabel:   { fontSize:'clamp(10px, 1.8vw, 11px)', color:'var(--text3)', fontWeight:600 },
  convVal:     { fontSize:'clamp(18px, 4vw, 22px)', fontWeight:800, letterSpacing:'-0.5px' },

  kpiStrip: { background:'var(--surface)', border:'1px solid var(--border)', borderRadius:14, padding:'18px 24px', display:'flex', alignItems:'center', gap:16, flexWrap:'wrap' },
  kpiItem:  { textAlign:'center', flex:1, minWidth:80 },
  kpiVal:   { fontSize:'clamp(18px, 3.5vw, 24px)', fontWeight:800, color:'var(--text)', letterSpacing:'-0.5px' },
  kpiLbl:   { fontSize:'clamp(9px, 1.6vw, 11px)', color:'var(--text3)', marginTop:3 },
  kpiDiv:   { fontSize:'clamp(16px, 3vw, 20px)', color:'var(--text3)' },

  funnelViz:  { background:'var(--surface)', border:'1px solid var(--border)', borderRadius:14, padding:24, display:'flex', flexDirection:'column', gap:0 },
  funnelStep: { display:'flex', flexDirection:'column', gap:8, paddingBottom:12, marginBottom:4 },
  funnelStepHeader:{ display:'flex', alignItems:'center', gap:14, flexWrap:'wrap' },
  funnelStepNum:   { width:34, height:34, borderRadius:9, display:'flex', alignItems:'center', justifyContent:'center', fontWeight:800, fontSize:'clamp(12px, 2vw, 14px)', flexShrink:0 },
  funnelStepInfo:  { flex:1, minWidth:120 },
  funnelStepName:  { fontSize:'clamp(12px, 2vw, 14px)', fontWeight:600, color:'var(--text)' },
  funnelEventName: { fontSize:'clamp(8px, 1.5vw, 10px)', color:'var(--text3)', fontFamily:'Space Mono,monospace', marginTop:2 },
  funnelStepStats: { textAlign:'right', minWidth:80 },
  funnelCount:     { fontSize:'clamp(16px, 3vw, 20px)', fontWeight:800, display:'block', letterSpacing:'-0.5px' },
  funnelUsers:     { fontSize:'clamp(8px, 1.5vw, 10px)', color:'var(--text3)', fontFamily:'Space Mono,monospace' },
  funnelBarTrack:  { background:'var(--surface2)', borderRadius:8, height:24, position:'relative', overflow:'hidden' },
  funnelBar:       { height:'100%', borderRadius:8, display:'flex', alignItems:'center', justifyContent:'flex-end', paddingRight:8, transition:'width 0.6s cubic-bezier(0.34,1.2,0.64,1)', minWidth:32 },
  funnelBarLabel:  { fontSize:'clamp(9px, 1.8vw, 11px)', color:'#fff', fontWeight:700, fontFamily:'Space Mono,monospace' },
  dropBadge:       { fontSize:'clamp(9px, 1.8vw, 11px)', paddingLeft:48, color:'var(--text3)' },

  tableWrap:  { display:'flex', flexDirection:'column', overflowX:'auto' },
  thead:      { display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(60px, 1fr))', padding:'10px 14px', fontSize:'clamp(8px, 1.5vw, 10px)', color:'var(--text3)', fontWeight:700, letterSpacing:'0.08em', textTransform:'uppercase', background:'var(--surface2)', borderRadius:8, gap:8 },
  trow:       { display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(60px, 1fr))', padding:'11px 14px', fontSize:'clamp(10px, 1.8vw, 12px)', color:'var(--text2)', borderBottom:'1px solid var(--border2)', gap:8, alignItems:'center' },
  stepBadge:  { padding:'3px 8px', borderRadius:6, fontSize:'clamp(9px, 1.6vw, 11px)', fontWeight:700, textAlign:'center', display:'inline-block' },

  insightsBox:  { background:'var(--surface)', border:'1px solid var(--border)', borderRadius:14, padding:20 },
  insightsTitle:{ fontSize:'clamp(12px, 2.5vw, 14px)', fontWeight:700, color:'var(--text)', marginBottom:14 },
  insightsList: { display:'flex', flexDirection:'column', gap:12 },
  insightItem:  { display:'flex', alignItems:'flex-start', gap:12, padding:'14px 16px', background:'var(--surface2)', border:'1px solid', borderRadius:12 },
};
